import type { NavItemProps, NavSectionProps } from 'src/components/nav-section';

import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';

import { useAuthContext } from 'src/auth/hooks';

import { splitNavData } from './nav-bottom';
import { navData as dealerNavData } from '../nav-config-dashboard-artham-user';
import { navData as adminNavData } from '../nav-config-dashboard-artham-admin';
import { navData as reviewerNavData } from '../nav-config-dashboard-artham-reviewer';
import { navData as distributorNavData } from '../nav-config-dashboard-artham-distributor';

// ----------------------------------------------------------------------

type Translate = (key: string) => string;

/** Resolve the role-specific dashboard nav tree (matches the sidebar + bottom nav). */
export function getNavDataByRole(
  userRole: string | null | undefined,
  t: Translate
): NavSectionProps['data'] {
  if (userRole === 'ADMIN') return adminNavData(t);
  if (userRole === 'DISTRIBUTOR') return distributorNavData(t);
  if (userRole === 'REVIEWER') return reviewerNavData(t);
  return dealerNavData(t);
}

/** Permission predicate used to filter nav items down to the current role. */
export function makeCanDisplayItemByRole(userRole: string | null | undefined) {
  return (allowedRoles: NavItemProps['allowedRoles']): boolean => {
    if (!allowedRoles) return true;
    if (Array.isArray(allowedRoles)) return allowedRoles.includes(userRole || '');
    return allowedRoles === userRole;
  };
}

/**
 * Path of the first tab on the current role's bottom navigation bar.
 *
 * Entry routes (`/`, `/dashboard`) redirect here so users land on a real nav
 * destination and the matching bottom-nav tab highlights - instead of an orphan
 * page that maps to no tab. Derived from the same navData + splitNavData the
 * bottom nav renders, so it stays in sync as configs change. `undefined` while
 * the role is still resolving.
 */
export function useDefaultDashboardPath(): string | undefined {
  const { userRole } = useAuthContext();
  const { t } = useTranslate('nav');

  return useMemo(() => {
    const navData = getNavDataByRole(userRole, t);
    const { visible } = splitNavData(navData, makeCanDisplayItemByRole(userRole));
    return visible[0]?.path;
  }, [userRole, t]);
}

/**
 * Where a role's app-launch entry (`/`) opens, when that differs from the first
 * bottom-nav tab.
 *
 * A dealer's job is sending money, so the app opens on the send flow - but their
 * first tab stays Home so `/dashboard` keeps rendering the dealer overview
 * instead of redirecting into the flow. Roles absent here fall back to the first
 * tab, which is what every entry route did before.
 */
const LAUNCH_PATH_BY_ROLE: Record<string, string> = {
  DEALER: paths.dashboard.artham.send,
};

/** Path the app-launch entry route (`/`) redirects to. `undefined` while the role is resolving. */
export function useAppLaunchPath(): string | undefined {
  const { userRole } = useAuthContext();
  const { t } = useTranslate('nav');

  return useMemo(() => {
    const navData = getNavDataByRole(userRole, t);
    const { visible } = splitNavData(navData, makeCanDisplayItemByRole(userRole));
    const fallback = visible[0]?.path;
    if (!fallback) return undefined;
    return (userRole && LAUNCH_PATH_BY_ROLE[userRole]) || fallback;
  }, [userRole, t]);
}
